/**
 * =============================================================================
 * Hook: useReferralPayoutsPaginated
 * Versão: 1.0.0
 *
 * Descrição:
 *   Hook server-side para listagem paginada de solicitações de saque
 *   (affiliate_payouts) do tenant ativo. Espelha o padrão de
 *   `useFinanceiroCobrancasPaginated` (módulo Financeiro):
 *
 *   - Chama a RPC `get_referral_payouts_paginated`, que devolve em UMA única
 *     chamada: linhas paginadas + total_count + KPIs globais (window functions).
 *   - 100% server-side: nenhum SELECT direto na tabela é feito a partir do
 *     frontend, evitando vazamento de dados sensíveis e o limite de 1k rows
 *     do client.
 *
 * Release notes:
 *   v1.0.0 - Criação do hook (substitui a query de listagem de
 *            useReferralPayoutsAdmin, que passa a ser deprecada).
 * =============================================================================
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { Database } from '@/integrations/supabase/types';

// Logger centralizado para depuração
const logger = createLogger('ReferralPayoutsPaginated');

export type PayoutStatus = Database['public']['Enums']['affiliate_payout_status'];
export type PayoutMethod = Database['public']['Enums']['affiliate_payout_method'];

/**
 * Estrutura flat retornada pela RPC `get_referral_payouts_paginated`.
 * Otimizada para uso direto em tabelas (sem embeds aninhados).
 */
export interface PaginatedPayout {
  // Dados do payout
  id: string;
  tenant_id: string;
  affiliate_id: string;
  amount: number;
  currency: string;
  status: PayoutStatus;
  payout_method: PayoutMethod;
  commissions_count: number;
  requested_at: string;
  processed_at: string | null;
  paid_at: string | null;
  failed_reason: string | null;
  notes: string | null;
  external_transaction_id: string | null;

  // Campos do JOIN com affiliates (flat)
  affiliate_full_name: string | null;
  affiliate_email: string | null;
  affiliate_pix_key: string | null;
  affiliate_pix_key_type: string | null;

  /**
   * Resumo das comissões vinculadas ao payout (top 5 por reward_value).
   * Permite mostrar a "origem" do pagamento (negócio/cliente/lead/indicação) direto na listagem.
   * Campos `customer_cpf`, `customer_cnpj` e `referral_id` vêm da RPC v1.2.0+.
   */
  commissions_summary: Array<{
    commission_id: string;
    reward_value: number | null;
    deal_id: string | null;
    deal_title: string | null;
    customer_id: string | null;
    customer_name: string | null;
    customer_cpf?: string | null;   // v1.2.0
    customer_cnpj?: string | null;  // v1.2.0
    lead_name: string | null;
    referral_id?: string | null;    // v1.2.0
  }>;

  // KPIs globais (mesmo valor em todas as rows)
  kpi_total_pendente: number;
  kpi_total_processando: number;
  kpi_total_pago: number;
  kpi_total_falho_cancelado: number;
  kpi_count_total: number;

  // Paginação (window function)
  total_count: number;
}

/** Filtros aceitos pela listagem de pagamentos. */
export interface PayoutPaginatedFilters {
  status?: PayoutStatus | null;
  affiliate_id?: string | null;
  payout_method?: PayoutMethod | null;
  search?: string | null;
  data_solicitacao_de?: string | null;
  data_solicitacao_ate?: string | null;
  data_pagamento_de?: string | null;
  data_pagamento_ate?: string | null;
}

/** KPIs agregados retornados pela RPC. */
export interface PayoutKpis {
  totalPendente: number;
  totalProcessando: number;
  totalPago: number;
  totalFalhoCancelado: number;
  countTotal: number;
}

interface PaginationParams {
  page: number;
  pageSize: number;
}

export interface PaginatedPayoutsResult {
  payouts: PaginatedPayout[];
  totalCount: number;
  totalPages: number;
  kpis: PayoutKpis;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
}

const EMPTY_KPIS: PayoutKpis = {
  totalPendente: 0,
  totalProcessando: 0,
  totalPago: 0,
  totalFalhoCancelado: 0,
  countTotal: 0,
};

/**
 * Hook principal — listagem paginada de pagamentos (server-side).
 *
 * @param filters    Filtros aplicados pela RPC.
 * @param pagination Página e tamanho da página.
 */
export function useReferralPayoutsPaginated(
  filters: PayoutPaginatedFilters | undefined,
  pagination: PaginationParams,
): PaginatedPayoutsResult {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [
      'referral-payouts-paginated',
      currentTenant?.id,
      filters,
      pagination,
    ],
    queryFn: async () => {
      logger.debug('Buscando payouts paginados', { filters, pagination });

      // Casting necessário até `types.ts` ser regenerado com a nova RPC
      const { data, error } = await (supabase.rpc as any)(
        'get_referral_payouts_paginated',
        {
          p_page: pagination.page,
          p_page_size: pagination.pageSize,
          p_tenant_hint: currentTenant?.id || null,
          p_status: filters?.status || null,
          p_affiliate_id: filters?.affiliate_id || null,
          p_payout_method: filters?.payout_method || null,
          p_search: filters?.search || null,
          p_data_solicitacao_de: filters?.data_solicitacao_de || null,
          p_data_solicitacao_ate: filters?.data_solicitacao_ate || null,
          p_data_pagamento_de: filters?.data_pagamento_de || null,
          p_data_pagamento_ate: filters?.data_pagamento_ate || null,
        },
      );

      if (error) {
        logger.error('Erro ao buscar payouts paginados', error);
        throw error;
      }

      const rows = (data || []) as PaginatedPayout[];
      const totalCount = Number(rows[0]?.total_count ?? 0);

      const kpis: PayoutKpis = rows.length
        ? {
            totalPendente: Number(rows[0]?.kpi_total_pendente ?? 0),
            totalProcessando: Number(rows[0]?.kpi_total_processando ?? 0),
            totalPago: Number(rows[0]?.kpi_total_pago ?? 0),
            totalFalhoCancelado: Number(rows[0]?.kpi_total_falho_cancelado ?? 0),
            countTotal: Number(rows[0]?.kpi_count_total ?? 0),
          }
        : EMPTY_KPIS;

      return {
        payouts: rows,
        totalCount,
        totalPages: Math.ceil(totalCount / pagination.pageSize) || 1,
        kpis,
      };
    },
    enabled: !!currentTenant?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    payouts: query.data?.payouts || [],
    totalCount: query.data?.totalCount || 0,
    totalPages: query.data?.totalPages || 1,
    kpis: query.data?.kpis || EMPTY_KPIS,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    refetch: () => {
      queryClient.invalidateQueries({
        queryKey: ['referral-payouts-paginated', currentTenant?.id],
      });
    },
  };
}
